import React from 'react';
import { TextField, TextFieldProps, InputAdornment, Box, Typography } from '@mui/material';

/**
 * Design System Color Constants
 */
const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_DARK: '#004d4c',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  BORDER_HOVER: '#D0D2D7',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
  DESTRUCTIVE: '#D32F2F',
  SUCCESS: '#49CC90',
  WARNING: '#FCA130',
};

export interface BaseTextFieldProps extends Omit<TextFieldProps, 'variant'> {
  /**
   * Icon to display before the input
   */
  startIcon?: React.ReactNode;
  /**
   * Icon to display after the input
   */
  endIcon?: React.ReactNode;
  /**
   * Helper text displayed below input
   */
  helperText?: string;
  /**
   * Show character count for multiline
   */
  showCharCount?: boolean;
  /**
   * Max characters for the field
   */
  maxCharacters?: number;
}

/**
 * BaseTextField Component
 *
 * Premium text input component following the Pulse API design system.
 * Supports single-line and multi-line text with consistent styling.
 *
 * @example
 * ```tsx
 * // Basic text input
 * <BaseTextField
 *   label="Email"
 *   placeholder="Enter your email"
 *   type="email"
 * />
 *
 * // With icons
 * <BaseTextField
 *   label="Search"
 *   placeholder="Search..."
 *   startIcon={<SearchIcon />}
 * />
 *
 * // Multiline with character count
 * <BaseTextField
 *   label="Description"
 *   multiline
 *   rows={4}
 *   showCharCount
 *   maxCharacters={500}
 * />
 * ```
 */
export const BaseTextField = React.forwardRef<HTMLDivElement, BaseTextFieldProps>(
  (
    {
      label,
      placeholder,
      error = false,
      disabled = false,
      required = false,
      helperText = '',
      startIcon,
      endIcon,
      multiline = false,
      rows,
      value = '',
      onChange,
      showCharCount = false,
      maxCharacters,
      fullWidth = true,
      size = 'medium',
      type = 'text',
      ...props
    },
    ref
  ) => {
    const charCount = typeof value === 'string' ? value.length : 0;
    const isNearLimit = !!(maxCharacters && charCount >= maxCharacters * 0.8);
    const isAtLimit = !!(maxCharacters && charCount >= maxCharacters);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Prevent input if at character limit
      if (maxCharacters && charCount >= maxCharacters && e.target.value.length > charCount) {
        return;
      }
      onChange?.(e as any);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {label && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {label}
            {required && <span style={{ color: COLORS.DESTRUCTIVE, marginLeft: '4px' }}>*</span>}
          </Typography>
        )}

        <TextField
          ref={ref}
          placeholder={placeholder}
          error={error || isAtLimit}
          disabled={disabled}
          required={required}
          fullWidth={fullWidth}
          multiline={multiline}
          rows={rows}
          value={value}
          onChange={handleChange}
          type={type}
          variant="outlined"
          size={size === 'small' ? 'small' : 'medium'}
          InputProps={{
            startAdornment: startIcon && (
              <InputAdornment position="start" sx={{ mr: 1, color: COLORS.TEXT_SECONDARY }}>
                {startIcon}
              </InputAdornment>
            ),
            endAdornment: endIcon && (
              <InputAdornment position="end" sx={{ ml: 1, color: COLORS.TEXT_SECONDARY }}>
                {endIcon}
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            sx: {
              display: 'none',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: '"Manrope", sans-serif',
              fontSize: '13px',
              color: COLORS.TEXT_PRIMARY,
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

              // Border states
              '& fieldset': {
                borderColor: COLORS.BORDER,
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: !disabled ? COLORS.BORDER_HOVER : COLORS.BORDER,
              },
              '&.Mui-focused fieldset': {
                borderColor: COLORS.ACCENT,
                borderWidth: '1.5px',
              },
              '&.Mui-error fieldset': {
                borderColor: COLORS.DESTRUCTIVE,
              },

              // Text color
              '& input': {
                color: COLORS.TEXT_PRIMARY,
                '&::placeholder': {
                  color: COLORS.TEXT_TERTIARY,
                  opacity: 0.7,
                },
              },

              // Disabled state
              '&.Mui-disabled': {
                backgroundColor: COLORS.SURFACE_SECONDARY,
                '& fieldset': {
                  borderColor: COLORS.BORDER,
                },
                '& input': {
                  color: COLORS.TEXT_TERTIARY,
                },
              },

              // Multiline
              '& textarea': {
                color: COLORS.TEXT_PRIMARY,
                '&::placeholder': {
                  color: COLORS.TEXT_TERTIARY,
                  opacity: 0.7,
                },
              },
            },
          }}
          {...props}
        />

        {/* Helper text and character count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {helperText && (
            <Typography
              sx={{
                fontSize: '11px',
                color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_TERTIARY,
                fontWeight: 500,
              }}
            >
              {helperText}
            </Typography>
          )}
          {showCharCount && maxCharacters && (
            <Typography
              sx={{
                fontSize: '11px',
                color: isAtLimit ? COLORS.DESTRUCTIVE : isNearLimit ? COLORS.WARNING : COLORS.TEXT_TERTIARY,
                fontWeight: 500,
                ml: 'auto',
              }}
            >
              {charCount} / {maxCharacters}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }
);

BaseTextField.displayName = 'BaseTextField';

export default BaseTextField;
